import React from 'react';
import { Button } from '../../atoms/Button/Button';
import './AttachmentStyles.scss';
/**
 * Returns a Font Awesome icon class based on file type
 */
function getIconClass(type) {
    switch (type) {
        case 'image':
            return 'fa-light fa-file-image';
        case 'audio':
            return 'fa-light fa-file-audio';
        case 'document':
        default:
            return 'fa-light fa-file-pdf';
    }
}
/**
 * Returns icon class and CSS modifier for a scan status
 */
function getScanStatusInfo(status) {
    switch (status) {
        case 'pending_scan':
            return { icon: 'fa-light fa-spinner fa-spin', modifier: 'pending', label: 'Scanning...' };
        case 'clean':
            return { icon: 'fa-light fa-circle-check', modifier: 'clean', label: 'Clean' };
        case 'infected':
            return { icon: 'fa-light fa-virus', modifier: 'infected', label: 'Infected' };
        case 'scan_error':
            return { icon: 'fa-light fa-triangle-exclamation', modifier: 'scan-error', label: 'Scan failed' };
        default:
            return { icon: '', modifier: '', label: '' };
    }
}
/**
 * Attachment Component
 * Displays a single file attachment chip with an icon, name, and optional remove button.
 * Use readOnly={true} to hide the remove button for display-only contexts.
 * @version 2.0.0
 * @since 2026-02-17
 */
export function Attachment({ id, name, type, readOnly = false, status, errorMessage, onRemove }) {
    const statusInfo = status ? getScanStatusInfo(status) : null;
    const isInfected = status === 'infected';
    const isErrored = status === 'scan_error';
    const classNames = [
        'qmb-ui-attachment',
        readOnly ? 'qmb-ui-attachment--read-only' : '',
        isInfected ? 'qmb-ui-attachment--infected' : '',
        isErrored ? 'qmb-ui-attachment--errored' : ''
    ]
        .filter(Boolean)
        .join(' ');
    // For failed uploads/scans, surface the server-side reason on the WHOLE
    // chip (title attribute) so hovering anywhere on the chip — not just the
    // tiny status icon — reveals why it was skipped.
    const chipTooltip = isErrored && errorMessage ? `${name} — ${errorMessage}` : name;
    const iconTooltip = isErrored && errorMessage ? errorMessage : statusInfo === null || statusInfo === void 0 ? void 0 : statusInfo.label;
    return (React.createElement("div", { className: classNames, title: chipTooltip },
        React.createElement("i", { className: `qmb-ui-attachment__icon ${getIconClass(type)}` }),
        React.createElement("span", { className: "qmb-ui-attachment__name" }, name),
        statusInfo && (React.createElement("i", { className: `qmb-ui-attachment__scan-status qmb-ui-attachment__scan-status--${statusInfo.modifier} ${statusInfo.icon}`, title: iconTooltip, "aria-label": iconTooltip })),
        !readOnly && onRemove && (React.createElement(Button, { className: "qmb-ui-attachment__remove", "aria-label": `Remove ${name}`, onClick: () => onRemove(id) },
            React.createElement("i", { className: "fa-light fa-trash-can" })))));
}
